import { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Training({ user }) {
  const [activeTab, setActiveTab] = useState('programs');
  const [programs, setPrograms] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [showEvaluateModal, setShowEvaluateModal] = useState(false);
  const [selectedEnrollment, setSelectedEnrollment] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [enrollForm, setEnrollForm] = useState({
    user_id: '',
    program_id: '',
    start_date: new Date().toISOString().split('T')[0],
  });

  const [evaluateForm, setEvaluateForm] = useState({
    enrollment_id: '',
    evaluation_type: 'Written Exam',
    score: '',
    comments: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [programsRes, enrollmentsRes, personnelRes] = await Promise.all([
        axios.get(`${API}/training/programs`),
        axios.get(`${API}/training/enrollments`),
        axios.get(`${API}/personnel`),
      ]);
      setPrograms(programsRes.data);
      setEnrollments(enrollmentsRes.data);
      setPersonnel(personnelRes.data);
    } catch (error) {
      console.error('Failed to fetch training data:', error);
      setError(error.response?.data?.detail || 'Failed to load training data');
    } finally {
      setLoading(false);
    }
  };

  const handleEnroll = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await axios.post(`${API}/training/enroll`, enrollForm);
      setSuccess('Personnel enrolled successfully!');
      setShowEnrollModal(false);
      setEnrollForm({ user_id: '', program_id: '', start_date: new Date().toISOString().split('T')[0] });
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to enroll personnel');
    }
  };

  const handleEvaluate = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await axios.post(`${API}/training/evaluate`, {
        ...evaluateForm,
        score: parseFloat(evaluateForm.score),
      });
      setSuccess('Evaluation recorded successfully!');
      setShowEvaluateModal(false);
      setEvaluateForm({ enrollment_id: '', evaluation_type: 'Written Exam', score: '', comments: '' });
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to record evaluation');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      'Enrolled': 'bg-blue-900/50 text-blue-300 border-blue-700',
      'In Progress': 'bg-amber-900/50 text-amber-300 border-amber-700',
      'Completed': 'bg-emerald-900/50 text-emerald-300 border-emerald-700',
      'Failed': 'bg-red-900/50 text-red-300 border-red-700',
      'Disqualified': 'bg-red-900/50 text-red-300 border-red-700',
    };
    return styles[status] || 'bg-slate-900/50 text-slate-300 border-slate-700';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="training-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Training & Academy</h1>
          <p className="text-slate-400">Guardian Academy programs and personnel training management</p>
        </div>
        {user.rank?.level >= 2 && (
          <button
            onClick={() => setShowEnrollModal(true)}
            className="btn-gold flex items-center gap-2"
            data-testid="enroll-button"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Enroll Personnel
          </button>
        )}
      </div>

      {/* Alerts */}
      {error && (
        <div className="bg-red-900/30 border border-red-700 text-red-300 px-4 py-3 rounded-lg animate-fade-in">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-emerald-900/30 border border-emerald-700 text-emerald-300 px-4 py-3 rounded-lg animate-fade-in">
          {success}
        </div>
      )}

      {/* Tab Navigation */}
      <div className="tab-nav">
        <button
          onClick={() => setActiveTab('programs')}
          className={`tab-button ${activeTab === 'programs' ? 'active' : ''}`}
          data-testid="tab-programs"
        >
          Training Programs
        </button>
        <button
          onClick={() => setActiveTab('enrollments')}
          className={`tab-button ${activeTab === 'enrollments' ? 'active' : ''}`}
          data-testid="tab-enrollments"
        >
          Enrollments ({enrollments.length})
        </button>
      </div>

      {/* Programs Tab */}
      {activeTab === 'programs' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {programs.map((program) => (
            <div key={program.id} className="military-card p-6" data-testid={`program-${program.code}`}>
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-xl font-bold text-white">{program.name}</h3>
                  <span className="inline-block px-2 py-1 bg-amber-500/20 text-amber-400 text-xs font-semibold rounded mt-1">
                    {program.code}
                  </span>
                </div>
                {program.is_required_for_guardianship && (
                  <span className="px-3 py-1 bg-red-900/50 text-red-300 text-xs font-semibold rounded border border-red-700">
                    REQUIRED
                  </span>
                )}
              </div>
              <p className="text-slate-400 text-sm mb-4">{program.description}</p>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-slate-500">Duration</span>
                  <p className="text-white font-medium">{program.duration_days} days ({program.duration_hours} hrs)</p>
                </div>
                <div>
                  <span className="text-slate-500">Pass Threshold</span>
                  <p className="text-white font-medium">{program.pass_threshold}%</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Enrollments Tab */}
      {activeTab === 'enrollments' && (
        <div className="military-card overflow-hidden">
          <table className="military-table">
            <thead>
              <tr>
                <th>Personnel</th>
                <th>Program</th>
                <th>Start Date</th>
                <th>Status</th>
                <th>Score</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {enrollments.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center py-8 text-slate-500">
                    No enrollments found
                  </td>
                </tr>
              ) : (
                enrollments.map((enrollment) => {
                  const person = personnel.find(p => p.id === enrollment.user_id);
                  const program = programs.find(p => p.id === enrollment.program_id);
                  return (
                    <tr key={enrollment.id} data-testid={`enrollment-${enrollment.id}`}>
                      <td className="text-white font-medium">{person?.full_name || 'Unknown'}</td>
                      <td>{program?.name || 'Unknown'}</td>
                      <td>{enrollment.start_date || 'Not set'}</td>
                      <td>
                        <span className={`px-2 py-1 text-xs font-medium rounded border ${getStatusBadge(enrollment.status)}`}>
                          {enrollment.status}
                        </span>
                      </td>
                      <td>
                        {enrollment.overall_score !== null ? (
                          <span className={enrollment.overall_score >= 70 ? 'text-emerald-400' : 'text-red-400'}>
                            {enrollment.overall_score?.toFixed(1)}%
                          </span>
                        ) : '-'}
                      </td>
                      <td>
                        {enrollment.status !== 'Completed' && enrollment.status !== 'Failed' && user.rank?.level >= 2 && (
                          <button
                            onClick={() => {
                              setSelectedEnrollment(enrollment);
                              setEvaluateForm({ ...evaluateForm, enrollment_id: enrollment.id });
                              setShowEvaluateModal(true);
                            }}
                            className="text-amber-400 hover:text-amber-300 text-sm font-medium"
                            data-testid={`evaluate-btn-${enrollment.id}`}
                          >
                            Evaluate
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Enroll Modal */}
      {showEnrollModal && (
        <div className="modal-overlay" data-testid="enroll-modal">
          <div className="modal-content p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-white">Enroll Personnel</h2>
              <button onClick={() => setShowEnrollModal(false)} className="text-slate-400 hover:text-white">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <form onSubmit={handleEnroll} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Personnel</label>
                <select
                  value={enrollForm.user_id}
                  onChange={(e) => setEnrollForm({ ...enrollForm, user_id: e.target.value })}
                  required
                  className="input-military"
                  data-testid="enroll-personnel-select"
                >
                  <option value="">Select Personnel</option>
                  {personnel.map((p) => (
                    <option key={p.id} value={p.id}>{p.full_name} ({p.service_number})</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Program</label>
                <select
                  value={enrollForm.program_id}
                  onChange={(e) => setEnrollForm({ ...enrollForm, program_id: e.target.value })}
                  required
                  className="input-military"
                  data-testid="enroll-program-select"
                >
                  <option value="">Select Program</option>
                  {programs.map((p) => (
                    <option key={p.id} value={p.id}>{p.name} ({p.code})</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Start Date</label>
                <input
                  type="date"
                  value={enrollForm.start_date}
                  onChange={(e) => setEnrollForm({ ...enrollForm, start_date: e.target.value })}
                  required
                  className="input-military"
                  data-testid="enroll-date-input"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button type="button" onClick={() => setShowEnrollModal(false)} className="btn-military">
                  Cancel
                </button>
                <button type="submit" className="btn-gold" data-testid="submit-enroll">
                  Enroll
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Evaluate Modal */}
      {showEvaluateModal && (
        <div className="modal-overlay" data-testid="evaluate-modal">
          <div className="modal-content p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-white">Record Evaluation</h2>
              <button onClick={() => setShowEvaluateModal(false)} className="text-slate-400 hover:text-white">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <form onSubmit={handleEvaluate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Evaluation Type</label>
                <select
                  value={evaluateForm.evaluation_type}
                  onChange={(e) => setEvaluateForm({ ...evaluateForm, evaluation_type: e.target.value })}
                  className="input-military"
                  data-testid="evaluation-type-select"
                >
                  <option value="Written Exam">Written Exam</option>
                  <option value="Practical Assessment">Practical Assessment</option>
                  <option value="Field Evaluation">Field Evaluation</option>
                  <option value="Instructor Grading">Instructor Grading</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Score (0-100)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={evaluateForm.score}
                  onChange={(e) => setEvaluateForm({ ...evaluateForm, score: e.target.value })}
                  required
                  className="input-military"
                  placeholder="Enter score"
                  data-testid="score-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Comments</label>
                <textarea
                  value={evaluateForm.comments}
                  onChange={(e) => setEvaluateForm({ ...evaluateForm, comments: e.target.value })}
                  className="input-military h-24 resize-none"
                  placeholder="Evaluation notes..."
                  data-testid="comments-input"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button type="button" onClick={() => setShowEvaluateModal(false)} className="btn-military">
                  Cancel
                </button>
                <button type="submit" className="btn-gold" data-testid="submit-evaluate">
                  Submit Evaluation
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Training;
